import { Payment } from '@inhouse-market/sdk';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import api from '../../services/apiSDK';
import { HIDDEN_LOGIN_RECAPTCHA_ACTION, hiddenLogin } from '../../services/hiddenLogin';
import { GetRecaptchaToken } from '../../utils/recaptcha';
import { queryKeys } from '../queryClient';

export async function refreshToken(getToken: GetRecaptchaToken) {
  try {
    return await hiddenLogin(getToken);
  } catch (error) {
    return null;
  }
}

export default function usePurchase() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const mutationFn = async (purchaseOption: Payment.CreatePurchase) => {
    let attempts = 0;

    while (attempts < 3) {
      try {
        const data = await api.customer.payment.purchase(purchaseOption);
        return data;
      } catch (error: any) {
        const status = error?.data?.response?.status;

        Sentry.captureException(error, {
          extra: {
            purchaseOption: JSON.stringify(purchaseOption),
            response: error?.data?.response ? JSON.stringify(error?.data?.response?.data) : null,
            status,
            headers: error?.data?.response ? JSON.stringify(error?.data?.response?.headers) : null,
            message: error?.data?.response?.data?.message,
            stack: error?.stack,
            attempts,
          },
        });

        if (status === 401 && attempts < 2) {
          const newToken = await refreshToken(
            async () => executeRecaptcha?.(HIDDEN_LOGIN_RECAPTCHA_ACTION),
          );
          if (!newToken) throw error;
          attempts++;
          continue;
        }

        throw error;
      }
    }
  };

  return useMutation({
    mutationFn,
    mutationKey: queryKeys.PURCHASE,
  });
}
